import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { CloudUpload, Clock, FileText, Plus, X } from "lucide-react";
import Swal from "sweetalert2";
import { companyFormToast, companyFormToastError } from "./companyFormSwal";
import { usePermissions } from "../../utilis/PermissionsContext";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import CompanyFormsPageHeader from "./CompanyFormsPageHeader";
import CompanyFormsEmptyState from "./CompanyFormsEmptyState";
import CompanyFormsInfoBanner from "./CompanyFormsInfoBanner";
import CompanyFormsStatsRow from "./CompanyFormsStatsRow";
import { CompanyFormsManagePageSkeleton } from "./CompanyFormsSkeletons";
import CompanyFormsEditFormModal from "./CompanyFormsEditFormModal";
import CompanyFormsDialogSelect from "./CompanyFormsDialogSelect";
import CompanyFormsVersionTable from "./CompanyFormsVersionTable";
import { useCompanyFormCategories } from "./useCompanyFormCategories";
import { useCompanyFormDestinations } from "./useCompanyFormDestinations";
import Paginations from "../GenericComponents/Pagination";
import {
  ACCEPTED_FORM_EXTENSIONS,
  ACCEPTED_FORM_TYPES,
  COMPANY_FORMS_MODULE_CONTENT,
  COMPANY_FORMS_MODULE_PAGE_SHELL,
  COMPANY_FORMS_ROOT,
  COMPANY_FORM_GLOBAL_DESTINATION,
  MANAGE_STAT_CARDS,
  MAX_FORM_BYTES,
  MODULE_PAGE_SHELL_STYLE,
  canDeleteCompanyForm,
  canDeleteCompanyFormVersion,
  canBulkDeleteCompanyForms,
  canManageCompanyDocuments,
  canUploadCompanyForms,
  canApproveCompanyForms,
  normalizeManageIndexRows,
} from "./companyFormConstants";

export default function CompanyFormsManage() {
  const {
    categories: formCategories,
    categoryLabels,
  } = useCompanyFormCategories();
  const { destinations } = useCompanyFormDestinations();
  const fileInputRef = useRef(null);

  const { permissions, loading: permissionsLoading } = usePermissions();
  const permissionsReadyRef = useRef(false);
  const [manageRows, setManageRows] = useState([]);
  const [documentCount, setDocumentCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    category: "terms_conditions",
    destination_country: COMPANY_FORM_GLOBAL_DESTINATION,
    name: "",
    description: "",
    file: null,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
  });
  const [editForm, setEditForm] = useState(null);

  const canUpload = canUploadCompanyForms(permissions);
  const canApprove = canApproveCompanyForms(permissions);
  const canBulkDelete = canBulkDeleteCompanyForms(permissions);
  const canManageDocuments = canManageCompanyDocuments(permissions);
  const canAccess = canUpload || canApprove;

  const manageStats = useMemo(() => {
    const pending = manageRows.filter(
      (row) => row.version?.status === "pending_approval",
    ).length;
    return { total: documentCount, pending };
  }, [documentCount, manageRows]);

  const fetchManage = useCallback(async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const params = {
        page: pagination.currentPage,
        perPage: pagination.perPage,
      };
      const response = await apiUrl.get("/company-forms", { params });
      const { rows, documentCount: docCount } = normalizeManageIndexRows(response?.data);
      setManageRows(rows);
      setDocumentCount(docCount);

      const pageInfo = response?.data?.pagination;
      if (pageInfo) {
        setPagination((prev) => ({
          ...prev,
          total: pageInfo.total || 0,
          lastPage: pageInfo.lastPage || 1,
          currentPage: pageInfo.currentPage || prev.currentPage,
          perPage: pageInfo.perPage || prev.perPage,
        }));
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to load documents",
        icon: "error",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [canAccess, pagination.currentPage, pagination.perPage]);

  useEffect(() => {
    fetchManage();
  }, [fetchManage]);

  useEffect(() => {
    if (!permissionsLoading) {
      permissionsReadyRef.current = true;
    }
  }, [permissionsLoading]);

  useEffect(() => {
    if (!formCategories.length) return;
    const slugs = formCategories.map((c) => c.value);
    if (!slugs.includes(uploadForm.category)) {
      setUploadForm((prev) => ({ ...prev, category: slugs[0] }));
    }
  }, [formCategories, uploadForm.category]);

  useEffect(() => {
    if (!uploadOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [uploadOpen]);

  const perItem = manageRows.length;

  const handlePaginationCallback = (page, perPage) => {
    if (perPage && perPage !== "") {
      setPagination((prev) => ({
        ...prev,
        perPage: parseInt(perPage, 10),
        currentPage: 1,
      }));
    } else {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
      }));
    }
  };

  const resetUploadForm = () => {
    setUploadForm({
      category: "terms_conditions",
      destination_country: COMPANY_FORM_GLOBAL_DESTINATION,
      name: "",
      description: "",
      file: null,
    });
    setDragOver(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const pickFile = (file) => {
    if (!file) return;
    if (file.size > MAX_FORM_BYTES) {
      Swal.fire("File too large", "Maximum size is 25MB.", "warning");
      return;
    }
    const ext = `.${(file.name.split(".").pop() || "").toLowerCase()}`;
    if (!ACCEPTED_FORM_EXTENSIONS.includes(ext) && !ACCEPTED_FORM_TYPES.includes(file.type)) {
      Swal.fire("Invalid file", "Use PDF or Word (.doc, .docx).", "warning");
      return;
    }
    setUploadForm((prev) => ({ ...prev, file }));
  };

  const submitNewForm = async () => {
    if (!canUpload) return;
    if (!uploadForm.name.trim()) {
      Swal.fire("Required", "Enter a document name.", "warning");
      return;
    }
    if (!uploadForm.file) {
      Swal.fire("Required", "Select a file to upload.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const body = new FormData();
      body.append("category", uploadForm.category);
      body.append("destination_country", uploadForm.destination_country);
      body.append("name", uploadForm.name.trim());
      body.append("description", uploadForm.description.trim());
      body.append("file", uploadForm.file);

      await apiUrl.post("/company-forms", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      Swal.fire({
        title: "Submitted",
        text: "Document uploaded and awaiting approval.",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2500,
      });

      setUploadOpen(false);
      resetUploadForm();
      fetchManage();
    } catch (error) {
      Swal.fire(
        "Upload failed",
        error.response?.data?.message || "Could not upload document",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveFormEdit = async (payload) => {
    if (!editForm) return;
    setSubmitting(true);
    try {
      await apiUrl.patch(`/company-forms/${editForm.id}`, payload);
      Swal.fire({
        title: "Saved",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      setEditForm(null);
      fetchManage();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Update failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteForm = async () => {
    if (!editForm) return;
    const result = await Swal.fire({
      title: "Delete this document?",
      text: "All versions and files will be removed and will no longer appear under View documents.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete document",
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      await apiUrl.delete(`/company-forms/${editForm.id}`);
      Swal.fire({
        title: "Deleted",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      setEditForm(null);
      fetchManage();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Delete failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVersion = async (form, version) => {
    const result = await Swal.fire({
      title: "Delete this upload?",
      text: `Remove "${form.name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });
    if (!result.isConfirmed) return;

    setSubmitting(true);
    try {
      await apiUrl.delete(`/company-forms/versions/${version.id}`);
      Swal.fire({
        title: "Deleted",
        icon: "success",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      fetchManage();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Delete failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBulkDelete = async (rows) => {
    if (!canBulkDelete) return;
    setSubmitting(true);
    try {
      const deletable = rows.filter(({ version }) =>
        canDeleteCompanyFormVersion(version, permissions),
      );
      if (!deletable.length) {
        Swal.fire(
          "Nothing to delete",
          "Selected rows cannot be deleted with your current permissions.",
          "info",
        );
        return;
      }
      for (const { version } of deletable) {
        await apiUrl.delete(`/company-forms/versions/${version.id}`);
      }
      companyFormToast({ title: "Done", timer: 2000 });
      fetchManage();
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Bulk delete failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (permissionsLoading && !permissionsReadyRef.current) {
    return (
      <div className={COMPANY_FORMS_ROOT}>
        <CompanyFormsPageHeader
          icon={CloudUpload}
          title="Upload documents"
          subtitle="Register documents and submit new versions for approval."
        />
        <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
          <div className={COMPANY_FORMS_MODULE_CONTENT}>
            <CompanyFormsManagePageSkeleton showPendingBanner={false} />
          </div>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className={COMPANY_FORMS_ROOT}>
        <CompanyFormsPageHeader icon={CloudUpload} title="Upload documents" />
        <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
          <div className={`${COMPANY_FORMS_MODULE_CONTENT} py-6`}>
            <CompanyFormsEmptyState
              icon={FileText}
              title="Access restricted"
              description="Upload or approve permission is required to manage documents here."
            >
              {canApprove ? (
                <Link
                  to="/company-forms/approve"
                  className="company-forms-btn inline-flex rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white no-underline hover:bg-[#0069d9]"
                >
                  Approve documents
                </Link>
              ) : (
                <Link
                  to="/company-forms"
                  className="company-forms-btn inline-flex rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white no-underline hover:bg-[#0069d9]"
                >
                  View documents
                </Link>
              )}
            </CompanyFormsEmptyState>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={COMPANY_FORMS_ROOT}>
        <CompanyFormsPageHeader
          icon={CloudUpload}
          title="Upload documents"
          subtitle="Register documents and submit new versions for approval."
          actions={
            canUpload ? (
            <button
              type="button"
              onClick={() => {
                resetUploadForm();
                setUploadOpen(true);
              }}
              className="company-forms-btn inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-3.5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-[#0069d9]"
            >
              <Plus size={14} strokeWidth={2} aria-hidden />
              Upload document
            </button>
            ) : null
          }
        />

        <div className={COMPANY_FORMS_MODULE_PAGE_SHELL} style={MODULE_PAGE_SHELL_STYLE}>
          <div className={COMPANY_FORMS_MODULE_CONTENT}>
          {loading && manageRows.length === 0 && documentCount === 0 ? (
            <CompanyFormsManagePageSkeleton showPendingBanner={false} />
          ) : (
            <>
          {documentCount > 0 ? (
            <CompanyFormsStatsRow cards={MANAGE_STAT_CARDS} stats={manageStats} loading={false} />
          ) : null}

          {documentCount === 0 ? (
            <CompanyFormsEmptyState
              icon={CloudUpload}
              title="No documents yet"
              description="Upload your first document to start the approval workflow."
            >
              <button
                type="button"
                onClick={() => {
                  resetUploadForm();
                  setUploadOpen(true);
                }}
                className="company-forms-btn inline-flex items-center gap-1.5 rounded-lg bg-[#007BFF] px-4 py-2 text-xs font-semibold text-white hover:bg-[#0069d9]"
              >
                <Plus size={14} strokeWidth={2} aria-hidden />
                Upload document
              </button>
            </CompanyFormsEmptyState>
          ) : (
            <>
              <CompanyFormsInfoBanner bannerKey="manage-info" icon={Clock}>
                {manageStats.pending > 0 ? (
                  <>
                    <span className="font-medium text-[#14213D]">
                      <span className="font-semibold text-[#007BFF]">{manageStats.pending}</span>{" "}
                      {manageStats.pending === 1 ? "version is" : "versions are"} awaiting approval.
                    </span>
                    {canApprove ? (
                      <span className="text-[#64748B]">
                        {" "}
                        Open{" "}
                        <Link to="/company-forms/approve" className="font-semibold text-[#007BFF]">
                          Approve documents
                        </Link>{" "}
                        to review.
                      </span>
                    ) : null}
                  </>
                ) : (
                  <span className="font-medium text-[#14213D]">
                    <span className="font-semibold text-[#007BFF]">{documentCount}</span>{" "}
                    {documentCount === 1 ? "document" : "documents"} registered. Use row actions to
                    upload versions or edit details.
                  </span>
                )}
              </CompanyFormsInfoBanner>

              {manageRows.length === 0 ? (
                <CompanyFormsEmptyState
                  icon={FileText}
                  title="No records on this page"
                  description="Try another page or upload a new document."
                />
              ) : (
                <CompanyFormsVersionTable
                  manageRows={manageRows}
                  categoryLabels={categoryLabels}
                  permissions={permissions}
                  canUpload={canUpload}
                  canApprove={canApprove}
                  submitting={submitting}
                  tableMode="upload"
                  onBulkDelete={canBulkDelete ? handleBulkDelete : undefined}
                  onEditForm={setEditForm}
                  onDeleteVersion={handleDeleteVersion}
                />
              )}
            </>
          )}

          {pagination.total > 0 ? (
            <div className="mt-4">
              <Paginations
                currentPage={pagination.currentPage}
                itemsPerPage={pagination.perPage}
                lastPage={pagination.lastPage}
                onPageChange={handlePaginationCallback}
                totalItems={pagination.total}
                perItem={perItem}
                perPageOptions={["10", "25", "50", "100"]}
              />
            </div>
          ) : null}
          </>
          )}
          </div>
        </div>
      </div>

      {uploadOpen
        ? createPortal(
            <div className="tailwind-container">
              <div
                className="fixed inset-0"
                style={{ zIndex: 1060, backgroundColor: "rgba(0, 0, 0, 0.55)" }}
                onMouseDown={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                aria-hidden="true"
              />
              <div
                className={`${COMPANY_FORMS_ROOT} company-forms-modal-overlay`}
                style={{ zIndex: 1070 }}
              >
                <div
                  className="company-forms-dialog overflow-y-auto bg-white shadow-xl"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="upload-form-title"
                  onMouseDown={(e) => e.stopPropagation()}
                >
            <div className="company-forms-dialog-header">
              <h2 id="upload-form-title" className="company-forms-dialog-title">
                Upload document
              </h2>
              <button
                type="button"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                className="company-forms-dialog-close"
                aria-label="Close"
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            </div>

            <div className="company-forms-dialog-body">
              <div className="company-forms-dialog-fields">
                <div className="company-forms-dialog-grid-2">
                  <CompanyFormsDialogSelect
                    id="upload-form-category"
                    label="Category"
                    value={uploadForm.category}
                    onChange={(category) => setUploadForm((p) => ({ ...p, category }))}
                    options={formCategories}
                  />

                  <CompanyFormsDialogSelect
                    id="upload-form-destination"
                    label="Destination country"
                    value={uploadForm.destination_country}
                    onChange={(destination_country) =>
                      setUploadForm((p) => ({ ...p, destination_country }))
                    }
                    options={destinations}
                  />
                </div>

                <div>
                  <label className="company-forms-dialog-label" htmlFor="upload-form-name">
                    Document name
                  </label>
                  <input
                    id="upload-form-name"
                    type="text"
                    value={uploadForm.name}
                    onChange={(e) => setUploadForm((p) => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Client Service Agreement 2026"
                    className="company-forms-dialog-control"
                  />
                </div>

                <div>
                  <label className="company-forms-dialog-label" htmlFor="upload-form-description">
                    Description (optional)
                  </label>
                  <textarea
                    id="upload-form-description"
                    value={uploadForm.description}
                    onChange={(e) =>
                      setUploadForm((p) => ({ ...p, description: e.target.value }))
                    }
                    rows={2}
                    className="company-forms-dialog-control"
                  />
                </div>

                <div>
                  <label className="company-forms-dialog-label">File</label>
                  <div
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      pickFile(e.dataTransfer.files?.[0]);
                    }}
                    className={`company-forms-dialog-dropzone${
                      dragOver ? " company-forms-dialog-dropzone--active" : ""
                    }`}
                  >
                    <CloudUpload
                      size={28}
                      strokeWidth={1.5}
                      className={`mx-auto mb-2 ${dragOver ? "text-[#007BFF]" : "text-[#94A3B8]"}`}
                      aria-hidden
                    />
                    <p className="text-[11px] font-medium text-slate-700">
                      Drag & drop PDF or Word file
                    </p>
                    <p className="company-forms-dialog-hint">Max 25MB</p>
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="company-forms-btn mt-2 inline-flex items-center gap-1 rounded-md bg-white px-2.5 py-1 text-[10px] font-semibold text-[#007BFF] ring-1 ring-[#007BFF]/30"
                    >
                      Browse files
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept={ACCEPTED_FORM_EXTENSIONS.join(",")}
                      className="hidden"
                      onChange={(e) => pickFile(e.target.files?.[0])}
                    />
                    {uploadForm.file ? (
                      <p className="mt-2 truncate text-[10px] font-semibold text-emerald-700">
                        {uploadForm.file.name}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="company-forms-dialog-footer">
              <button
                type="button"
                onClick={() => {
                  setUploadOpen(false);
                  resetUploadForm();
                }}
                className="company-forms-btn rounded-md px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                style={{ border: "none" }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={submitNewForm}
                className="company-forms-btn rounded-md bg-[#007BFF] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#0069d9] disabled:opacity-60"
                style={{ border: "none" }}
              >
                {submitting ? "Uploading…" : "Submit for approval"}
              </button>
            </div>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}

      <CompanyFormsEditFormModal
        open={Boolean(editForm)}
        form={editForm}
        categories={formCategories}
        destinations={destinations}
        canChangeCategory={Boolean(canManageDocuments && editForm)}
        canChangeDestination={Boolean(canManageDocuments && editForm)}
        canDeleteForm={
          editForm
            ? canDeleteCompanyForm(editForm, permissions)
            : false
        }
        submitting={submitting}
        onClose={() => setEditForm(null)}
        onSave={handleSaveFormEdit}
        onDeleteForm={handleDeleteForm}
      />
    </>
  );
}
